import React, { useState, useRef, useEffect } from 'react';

const VideoComponent = ({ src, activePipVideo, setActivePipVideo, currentlyPlaying, setCurrentlyPlaying }) => {
  const [pipManuallyClosed, setPipManuallyClosed] = useState(false);
  const videoRef = useRef(null);

  // Handle play request
  const handlePlay = () => {
    if (videoRef.current && videoRef.current !== currentlyPlaying) {
      videoRef.current.play().catch(error => {
        console.error('Play request failed:', error);
      });
    }
  };

  // Handle pause request
  const handlePause = () => {
    if (videoRef.current && videoRef.current === currentlyPlaying) {
      videoRef.current.pause();
    }
  };

  // Toggle Picture-in-Picture mode
  const handlePipToggle = async () => {
    try {
      if (videoRef.current !== document.pictureInPictureElement) {
        // Exit PiP mode on the currently active PiP video if different
        if (activePipVideo && activePipVideo !== videoRef.current) {
          // Check if activePipVideo is a valid element and has exitPictureInPicture method
          if (typeof activePipVideo.exitPictureInPicture === 'function') {
            await activePipVideo.exitPictureInPicture();
          }
        }
        await videoRef.current.requestPictureInPicture();
        setActivePipVideo(videoRef.current);
        setCurrentlyPlaying(videoRef.current);
      } else {
        await document.exitPictureInPicture();
        setActivePipVideo(null);
        setCurrentlyPlaying(null);
      }
    } catch (error) {
      console.error('Failed to toggle Picture-in-Picture mode:', error);
    }
  };

  // Handle intersection changes for PiP
  const handleIntersection = async (entries) => {
    const [entry] = entries;
    if (
      !entry.isIntersecting &&
      videoRef.current &&
      !document.pictureInPictureElement &&
      !pipManuallyClosed &&
      currentlyPlaying === videoRef.current
    ) {
      try {
        // Check if activePipVideo is a valid element and has exitPictureInPicture method
        if (activePipVideo && activePipVideo !== videoRef.current) {
          if (typeof activePipVideo.exitPictureInPicture === 'function') {
            await activePipVideo.exitPictureInPicture();
          }
        }
        // Check if requestPictureInPicture is available and if it can be called
        if (document.pictureInPictureElement || videoRef.current.requestPictureInPicture) {
          await videoRef.current.requestPictureInPicture();
          setActivePipVideo(videoRef.current);
        }
      } catch (error) {
        console.error('Failed to enter Picture-in-Picture mode:', error);
      }
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: '0px',
      threshold: 0,
    });

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => {
      if (videoRef.current) {
        observer.unobserve(videoRef.current);
      }
    };
  }, [pipManuallyClosed, activePipVideo, currentlyPlaying]);

  // Close PiP mode on Escape key press
  const handlePipClose = async () => {
    if (document.pictureInPictureElement) {
      await document.exitPictureInPicture();
      setPipManuallyClosed(true);
      setActivePipVideo(null);
      setCurrentlyPlaying(null);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handlePipClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Sync video playback state with `currentlyPlaying`
  useEffect(() => {
    if (videoRef.current) {
      if (currentlyPlaying === videoRef.current) {
        handlePlay();
      } else {
        handlePause();
      }
    }
  }, [currentlyPlaying]);

  return (
    <div className='h-100'>
      <video
        ref={videoRef}
        width="auto"
        className='h-100'
        controls
        onPlay={() => {
          setCurrentlyPlaying(videoRef.current);
        }}
        onPause={() => {
          if (currentlyPlaying === videoRef.current) {
            setCurrentlyPlaying(null);
          }
        }}
      >
        <source src={src} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
  );
};

export default VideoComponent;
